import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import Layout, { AgentsContext } from '../../components/layout/Layout';
import { getAgent, getAgentTools, Agent, updateAgent, getSessions, Session, getChatHistory, ChatEvent, AvailableTool, getRequiredUserProvidersForAgent } from '../../lib/database';
import { saveLastUsedAgent } from '../../lib/lastUsedAgent';
import { useAuth } from '../../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import { v4 as uuidv4 } from 'uuid';
import UnifiedSidebar from './components/UnifiedSidebar';
import QuickParametersSidebar from './components/QuickParametersSidebar';
import ConnectionManagerDrawer from './components/ConnectionManagerDrawer';
import { CopyIcon } from '../../components/ui/simple-icons';

interface ToolInvocation {
  call: any;
  response: any;
}

interface Turn {
  id: string;
  userMessage: ChatEvent | null;
  modelMessage: ChatEvent | null;
  thoughts: { title: string, content: string }[];
  toolInvocations: ToolInvocation[];
  isThinking: boolean;
  showThoughts?: boolean;
  showTools?: boolean;
  thinkingDuration?: number; // in seconds
  error?: {
    message: string;
    technicalDetails: string;
  };
  showErrorDetails?: boolean;
  imageUrls?: string[]; // URLs for attached image previews
  fileNames?: string[]; // Names of attached files
}

// Aggregates raw events from Supabase into "turns" for UI rendering.
const aggregateEventsToTurns = (events: ChatEvent[]): Turn[] => {
  const turns = new Map<string, Turn>();
  const toolCalls = new Map<string, any>();
  const turnTimestamps = new Map<string, { userTimestamp: Date | null, modelTimestamps: Date[] }>();

  for (const event of events) {
    const invocationId = event.invocation_id;
    if (!turns.has(invocationId)) {
      turns.set(invocationId, {
        id: invocationId,
        userMessage: null,
        modelMessage: null,
        thoughts: [],
        toolInvocations: [],
        isThinking: false,
        showThoughts: false,
        showTools: false,
      });
      turnTimestamps.set(invocationId, { userTimestamp: null, modelTimestamps: [] });
    }

    const turn = turns.get(invocationId)!;
    const timestamps = turnTimestamps.get(invocationId)!;

    // Check if event.content and event.content.parts exist
    if (!event.content || !event.content.parts || event.content.parts.length === 0) {
      console.warn('Event missing content or parts:', event);
      continue;
    }

    const part = event.content.parts[0];

    if (event.content.role === 'user') {
      if (part && part.function_response) {
        const call = toolCalls.get(part.function_response.id);
        if (call) {
          turn.toolInvocations.push({ call, response: part.function_response.response });
          toolCalls.delete(part.function_response.id);
        }
      } else {
        if (!turn.userMessage) {
          turn.userMessage = event;
          timestamps.userTimestamp = new Date(event.timestamp);
        }
      }
    } else if (event.content.role === 'model') {
      if (part && part.function_call) {
        toolCalls.set(part.function_call.id, part.function_call);
      }

      if (!turn.modelMessage) {
        turn.modelMessage = event;
      } else {
        const newText = event.content.parts.map(p => p.text || '').join('');
        if (newText) {
          turn.modelMessage.content.parts.push({ text: newText });
        }
      }
      timestamps.modelTimestamps.push(new Date(event.timestamp));
    }
  }

  const finalTurns = Array.from(turns.values());
  for (const turn of finalTurns) {
    const timestamps = turnTimestamps.get(turn.id);
    if (timestamps && timestamps.userTimestamp && timestamps.modelTimestamps.length > 0) {
      const userStart = timestamps.userTimestamp.getTime();
      const modelEnd = Math.max(...timestamps.modelTimestamps.map(t => t.getTime()));
      const duration = (modelEnd - userStart) / 1000;
      if (duration >= 0) {
        turn.thinkingDuration = duration;
      }
    }
  }

  return finalTurns;
};

interface MainLayoutProps {}

// Helper to format duration
const formatDuration = (seconds: number): string | null => {
  const roundedSeconds = Math.round(seconds);
  if (roundedSeconds === 0) {
    return null;
  }
  if (roundedSeconds < 60) {
    return `Thought for ${roundedSeconds} seconds`;
  }
  const minutes = Math.floor(roundedSeconds / 60);
  const remainingSeconds = roundedSeconds % 60;
  return `Thought for ${minutes}m ${remainingSeconds}s`;
};

const MainLayout: React.FC<MainLayoutProps> = () => {
  const { session } = useAuth();
  const { agentId } = useParams<{ agentId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshAgents } = useContext(AgentsContext);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Turn[]>([]);
  const [currentAgent, setCurrentAgent] = useState<Agent | null>(null);
  const [agentTools, setAgentTools] = useState<(any & { available_tool?: AvailableTool })[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showParametersSidebar, setShowParametersSidebar] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  
  // User-level OAuth connection state
  const [requiredProviders, setRequiredProviders] = useState<{ name: string; provider: string }[]>([]);
  const [activeConnections, setActiveConnections] = useState<Record<string, string>>({});
  
  console.log('[AgentChatPage] RENDER. Active Connections:', activeConnections); // NEW LOG

  const [thinkingMessage, setThinkingMessage] = useState<string | null>(null);
  
  // Chat history states
  const [sessions, setSessions] = useState<Session[]>([]);
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState<{message: string, type: 'success' | 'error'} | null>(null);

  const sessionId = useRef<string>(uuidv4());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Image upload states (multi-file support)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // File validation constants
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = 10; // Max files per message
  const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/bmp'];
  const ALLOWED_DOC_TYPES = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain', 'text/markdown'];

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '3.5rem';
    }
  };

  // File upload handlers (multi-file support)
  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `${file.name}: troppo grande (max 10MB)` };
    }

    const allAllowedTypes = [...ALLOWED_IMAGE_TYPES, ...ALLOWED_DOC_TYPES];
    if (!allAllowedTypes.includes(file.type)) {
      return { valid: false, error: `${file.name}: tipo non supportato` };
    }

    return { valid: true };
  };

  // Helper to add multiple files with validation
  const addFiles = (files: File[]) => {
    const currentCount = selectedFiles.length;
    const availableSlots = MAX_FILES - currentCount;

    if (availableSlots <= 0) {
      showToast(`Massimo ${MAX_FILES} file per messaggio`, 'error');
      return;
    }

    const filesToAdd = files.slice(0, availableSlots);
    const validFiles: File[] = [];
    const newPreviewUrls: string[] = [];
    const errors: string[] = [];

    for (const file of filesToAdd) {
      const validation = validateFile(file);
      if (!validation.valid) {
        errors.push(validation.error!);
        continue;
      }
      validFiles.push(file);
      // Create preview only for images
      if (ALLOWED_IMAGE_TYPES.includes(file.type)) {
        newPreviewUrls.push(URL.createObjectURL(file));
      } else {
        newPreviewUrls.push(''); // Empty string for non-image files
      }
    }

    if (errors.length > 0) {
      showToast(errors.join('\n'), 'error');
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      setPreviewUrls(prev => [...prev, ...newPreviewUrls]);

      if (files.length > availableSlots) {
        showToast(`Aggiunti ${validFiles.length} file (limite: ${MAX_FILES})`, 'success');
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    addFiles(files);
    e.target.value = ''; // Reset input for re-selection
  };

  const clearSelectedFiles = () => {
    previewUrls.forEach(url => {
      if (url) URL.revokeObjectURL(url);
    });
    setSelectedFiles([]);
    setPreviewUrls([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index]);
    }
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Drag & drop handlers
  const dragCounterRef = useRef(0);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      addFiles(files);
    }
  };

  // Paste handler for images (Ctrl+V)
  const handlePaste = (e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imagesToAdd: File[] = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (!file) continue;

        // Create a proper filename for pasted images
        const extension = file.type.split('/')[1] || 'png';
        const pastedFile = new File([file], `screenshot_${Date.now()}_${i}.${extension}`, { type: file.type });
        imagesToAdd.push(pastedFile);
      }
    }

    if (imagesToAdd.length > 0) {
      e.preventDefault();
      addFiles(imagesToAdd);
      showToast(`${imagesToAdd.length} immagine/i incollata/e`, 'success');
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleNewChat = () => {
    setCurrentSessionId(null);
    setMessages([]);
    sessionId.current = uuidv4(); // Assign a new session ID for the new chat
  };



  const handleSessionSelect = async (sessionIdToSelect: string) => {
    if (!currentAgent || !session?.user?.id) return;

    setCurrentSessionId(sessionIdToSelect);
    sessionId.current = sessionIdToSelect; // Ensure the ref is also updated
    setMessages([]); // Clear current messages
    
    try {
      const events = await getChatHistory(sessionIdToSelect, session.user.id);
      const turns = aggregateEventsToTurns(events);
      setMessages(turns);
    } catch (error) {
      console.error("Failed to load chat history for session:", sessionIdToSelect, error);
      showToast('Failed to load chat history.', 'error');
    }
  };

  const loadAgent = async (agentId: string, preserveCurrentSession = true) => {
    console.log(`[ChatPage] loadAgent called with agentId: ${agentId}, preserve: ${preserveCurrentSession}`);
    if (!agentId) {
      console.log('[ChatPage] agentId is missing, exiting loadAgent.');
      return;
    }

    try {
      setIsLoading(true);
      const agent = await getAgent(agentId);
      console.log('[ChatPage] getAgent returned:', agent);

      if (agent) {
        setCurrentAgent(agent);
        const tools = await getAgentTools(agent.id);
        console.log('[ChatPage] getAgentTools returned:', tools);
        setAgentTools(tools);

        // Efficiently fetch only the required user-level OAuth providers
        const providers = await getRequiredUserProvidersForAgent(agent.id);
        console.log('[AgentChatPage] Fetched required providers:', providers);
        setRequiredProviders(providers);

        // Update page title
        document.title = `${agent.name} - NoCo Chatbot`;

        // Save as last used agent
        saveLastUsedAgent(agent.id);

        // Load sessions for the agent with preservation flag
        if (session?.user?.id) {
          await loadSessionsForAgent(session.user.id, agent.id, preserveCurrentSession);
        }
      } else {
        console.log('[ChatPage] Agent not found, did not fetch providers.');
      }
    } catch (error) {
      console.error('[ChatPage] Error loading agent:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Toast helper function
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000); // Auto hide after 4 seconds
  };

  const loadSessionsForAgent = async (userId: string, agentId: string, preserveCurrentSession = true) => {
    try {
      setIsHistoryLoading(true);
      const fetchedSessions = await getSessions(userId, agentId);
      setSessions(fetchedSessions);

      const sessionExistsInList = fetchedSessions.some(s => s.id === currentSessionId);

      // Only auto-select if no session AND preserve flag is false (intentional agent change)
      // or if current session doesn't exist in the new list
      if (!preserveCurrentSession || (!currentSessionId || !sessionExistsInList)) {
        if (fetchedSessions.length > 0) {
          await handleSessionSelect(fetchedSessions[0].id);
        } else {
          handleNewChat();
        }
      }
      // If preserveCurrentSession=true and currentSessionId exists in list, keep it

      setHistoryError(null);
    } catch (err) {
      setHistoryError('Failed to load chat history.');
      console.error(err);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  // New handler for agent selection from dropdown
  const handleAgentSelect = async (agentId: string) => {
    if (agentId === currentAgent?.id) return; // No change

    // Update URL first to prevent state mismatch
    window.history.replaceState(null, '', `/agent/${agentId}`);

    // Clear current state for intentional agent changes
    setCurrentSessionId(null);
    setMessages([]);
    sessionId.current = uuidv4();

    // Load the new agent without preserving session (intentional change)
    await loadAgent(agentId, false);
  };

  // Removed competing useEffect that caused duplicate session loading and unwanted refreshes

  useEffect(() => {
    const state = location.state as { agent?: Agent, justCreated?: boolean } | null;

    const fetchProviders = async (agentId: string) => {
      const providers = await getRequiredUserProvidersForAgent(agentId);
      console.log('[AgentChatPage] Fetched required providers:', providers);
      setRequiredProviders(providers);
    };

    if (state?.agent && state.agent.id === agentId) {
      // Use the passed agent data immediately to avoid flash
      setCurrentAgent(state.agent);
      setIsLoading(false);

      // Load tools and providers
      loadAgentTools(state.agent.id);
      fetchProviders(state.agent.id);
      saveLastUsedAgent(state.agent.id);
    } else {
      // Fallback to loading from database
      if (agentId) {
        // For page loads/refreshes, preserve current session state
        loadAgent(agentId, true);
      }
    }

    if (state?.justCreated) {
      refreshAgents();
      window.history.replaceState(null, '');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, location.state, refreshAgents]);

  const loadAgentTools = async (agentId: string) => {
    try {
      const tools = await getAgentTools(agentId);
      setAgentTools(tools);
    } catch (error) {
      console.error('Error loading agent tools:', error);
    }
  };

  const handleSendMessage = async (messageOverride?: string) => {
    const currentMessage = messageOverride || message;
    // --- Pre-flight checks ---
    // Allow sending with just files (no message required)
    if (!currentMessage.trim() && selectedFiles.length === 0) return;
    if (!currentAgent) {
      console.error("handleSendMessage called without a currentAgent.");
      showToast("Error: Agent not loaded.", "error");
      return;
    }
    if (!session) {
      showToast("Error: User not authenticated.", "error");
      return;
    }
    const agentId = currentAgent.id;

    // --- Optimistic UI Update ---
    const invocationId = uuidv4();
    const userId = session.user.id;
    const isNewChat = !currentSessionId;

    // Save files and image URLs before clearing (for display in chat AND for sending)
    const filesToSend = [...selectedFiles];
    const currentImageUrls = [...previewUrls];
    const currentFileNames = selectedFiles.map(f => f.name);

    const userTurn: Turn = {
      id: invocationId,
      userMessage: {
        id: uuidv4(),
        session_id: currentSessionId || sessionId.current,
        user_id: userId,
        invocation_id: invocationId,
        timestamp: new Date().toISOString(),
        author: 'user',
        content: { role: 'user', parts: [{ text: currentMessage }] },
        partial: false,
        turn_complete: true,
      },
      modelMessage: null,
      thoughts: [],
      toolInvocations: [],
      isThinking: true,
      showThoughts: false,
      imageUrls: currentImageUrls.length > 0 ? currentImageUrls : undefined,
      fileNames: currentFileNames.length > 0 ? currentFileNames : undefined,
    };
    setMessages(prev => [...prev, userTurn]);

    // Clear input only if it's a new message, not a retry
    if (!messageOverride) {
      setMessage('');
      resetTextareaHeight(); // Reset height when clearing message
      // Don't revoke URLs yet - we need them for display. Clear file state only.
      setSelectedFiles([]);
      setPreviewUrls([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }

    setThinkingMessage("Thinking...");

    // --- Backend Call ---
    const token = session.access_token;
    const url = process.env.REACT_APP_AI_BACKEND_URL + '/v1/chat';

    console.log("Sending request to backend with agent_id:", agentId);
    console.log("[handleSendMessage] Active Connections:", activeConnections);
    console.log("[handleSendMessage] URL:", url);
    console.log("[handleSendMessage] Origin:", window.location.origin);

    try {
      const formData = new FormData();
      formData.append('agent_id', agentId);
      formData.append('session_id', currentSessionId || sessionId.current);
      formData.append('message', currentMessage || (filesToSend.length > 0 ? 'Analizza questi file' : ''));
      // Ensure stream is a string 'true' or 'false' for multipart/form-data
      formData.append('stream', 'false');

      // Append files if selected (use saved reference, multi-file support)
      if (filesToSend.length > 0) {
        filesToSend.forEach(file => {
          formData.append('files', file);
        });
        console.log('[handleSendMessage] Sending files:', filesToSend.map(f => `${f.name} (${f.type}, ${f.size})`));
      }

      // Pass the active user-level connections
      if (Object.keys(activeConnections).length > 0) {
        formData.append('provider_connections', JSON.stringify(activeConnections));
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }, // No Content-Type, browser sets it
        body: formData,
      });

      if (!response.ok) {
        const errorBody = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, body: ${errorBody}`);
      }

      // --- JSON Response Processing ---
      const data = await response.json();
      const messageContent = data.choices[0]?.message?.content;

      if (!messageContent) {
        throw new Error("Invalid response structure from backend.");
      }

      setThinkingMessage(null);

      setMessages(prev => {
        const updatedMessages = [...prev];
        const lastTurn = updatedMessages[updatedMessages.length - 1];
        if (lastTurn && lastTurn.id === invocationId) {
          const modelTimestamp = new Date();
          const userTimestamp = new Date(lastTurn.userMessage!.timestamp);
          const durationInSeconds = (modelTimestamp.getTime() - userTimestamp.getTime()) / 1000;

          lastTurn.isThinking = false;
          lastTurn.thinkingDuration = durationInSeconds;
          lastTurn.modelMessage = {
            id: uuidv4(),
            session_id: currentSessionId || sessionId.current,
            user_id: userId,
            invocation_id: invocationId,
            timestamp: modelTimestamp.toISOString(),
            author: 'model',
            content: { role: 'model', parts: [{ text: messageContent }] },
            partial: false,
            turn_complete: true,
          };
        }
        return updatedMessages;
      });


      // If it was a new chat, refresh the session list
      if (isNewChat) {
        // TODO: This is a temporary solution. Ideally, the backend would return the new session info.
        await getSessions(userId, agentId).then(setSessions);
        setCurrentSessionId(sessionId.current);
      } else {
        // If it's an existing chat, just refresh the list to update the order
        getSessions(userId, agentId).then(setSessions);
      }

    } catch (error: any) {
      console.error("Fetch stream failed:", error);

      const userFriendlyMessage = "An error occurred while sending the message.";
      const technicalDetails = error.stack || error.toString();

      showToast(userFriendlyMessage, 'error');
      setThinkingMessage(null);
      
      // Update the turn with an error state instead of removing it
      setMessages(prev => prev.map(turn => {
        if (turn.id === invocationId) {
          return {
            ...turn,
            isThinking: false,
            error: {
              message: userFriendlyMessage,
              technicalDetails: technicalDetails,
            },
          };
        }
        return turn;
      }));
    }
  };

  const handleRetrySendMessage = async (messageContent: string, failedTurnId: string) => {
    // Remove the failed turn from the UI before retrying
    setMessages(prev => prev.filter(turn => turn.id !== failedTurnId));
    await handleSendMessage(messageContent);
  };

  const handleSave = async (params: { instruction: string; temperature: number; max_tokens: number }) => {
    if (!agentId || isSaving) return;

    try {
      setIsSaving(true);
      await updateAgent(agentId, params);

      // Update currentAgent state locally to reflect changes
      if (currentAgent) {
        setCurrentAgent({ ...currentAgent, ...params });
      }

      showToast('Agent updated successfully!', 'success');
      setShowParametersSidebar(false);
    } catch (error) {
      console.error('Error saving agent parameters:', error);
      showToast('Failed to save changes.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReloadChat = async () => {
    if (!currentSessionId || !session?.user?.id) {
      showToast('No active chat session to reload', 'error');
      return;
    }

    try {
      setIsHistoryLoading(true);
      setMessages([]); // Clear current messages

      // Reload chat history from database
      const events = await getChatHistory(currentSessionId, session.user.id);
      const turns = aggregateEventsToTurns(events);
      setMessages(turns);

      showToast('Chat reloaded successfully', 'success');
    } catch (error) {
      console.error('Failed to reload chat:', error);
      showToast('Failed to reload chat from database', 'error');
    } finally {
      setIsHistoryLoading(false);
    }
  };





  if (isLoading) {
    return (
      <Layout title="Loading...">
        <div className="flex-1 flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-netlify-teal border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!currentAgent) {
    return (
      <Layout title="Agent Not Found">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Agent Not Found</h2>
            <p className="text-gray-600">The requested agent could not be found.</p>
          </div>
        </div>
      </Layout>
    );
  }


  return (
    <Layout title={currentAgent.name}>
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-5 right-5 px-6 py-3 rounded-lg shadow-lg text-white z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          {toast.message}
        </div>
      )}

      <UnifiedSidebar
        currentAgent={currentAgent}
        sessions={sessions}
        currentSessionId={currentSessionId}
        isHistoryLoading={isHistoryLoading}
        historyError={historyError}
        showMobileSidebar={showMobileSidebar}
        onAgentSelect={handleAgentSelect}
        onSessionSelect={handleSessionSelect}
        onNewChat={handleNewChat}
        onCloseMobile={() => setShowMobileSidebar(false)}
      />

      {/* Main Chat Interface */}
      <div className={`flex-1 flex flex-col bg-white transition-all duration-300 ${showParametersSidebar ? 'lg:mr-80' : ''}`}> 
        {/* Clean Chat Header */}
        <div className="px-4 lg:px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Mobile menu button */}
              <button
                onClick={() => setShowMobileSidebar(true)}
                className="lg:hidden p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">{currentAgent.name}</h2>
                <p className="text-sm text-gray-500 mt-1">Ready to chat</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate(`/edit/${currentAgent.id}?tab=4`)}
                className="hidden sm:flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12s-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-8.316l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"></path></svg>
                <span>Share</span>
              </button>
              <button
                onClick={handleReloadChat}
                disabled={!currentSessionId || isHistoryLoading}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Reload chat from database"
              >
                <svg className={`w-4 h-4 ${isHistoryLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span className="hidden sm:inline">Ricarica</span>
              </button>
              <button
                onClick={() => setShowParametersSidebar(!showParametersSidebar)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${showParametersSidebar
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto bg-white">
          <div className="max-w-full md:max-w-4xl mx-auto px-2 md:px-4 lg:px-6 py-4 lg:py-8">
            {messages.length === 0 ? (
              <div className="text-center py-12">
                <div className={`w-16 h-16 rounded-full ${currentAgent.color} flex items-center justify-center mx-auto mb-4`}>
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Start a conversation</h3>
                <p className="text-gray-600">Send a message to begin chatting with {currentAgent.name}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((turn) => (
                  <div key={turn.id}>
                    {/* User Message */}
                    {turn.userMessage && (
                      <div className="flex justify-end">
                        <div className={`rounded-2xl px-4 py-3 max-w-sm sm:max-w-md ${turn.error ? 'bg-red-200' : 'bg-teal-700'}`}>
                          {/* Show attached images (grid for multiple) */}
                          {turn.imageUrls && turn.imageUrls.some(url => url) && (
                            <div className={`mb-2 ${turn.imageUrls.filter(url => url).length > 1 ? 'grid grid-cols-2 gap-1' : ''}`}>
                              {turn.imageUrls.map((url, idx) => url && (
                                <img
                                  key={idx}
                                  src={url}
                                  alt={turn.fileNames?.[idx] || `Attached image ${idx + 1}`}
                                  className="max-w-full rounded-lg max-h-32 object-cover"
                                />
                              ))}
                            </div>
                          )}
                          {/* Show file names for non-image files */}
                          {turn.fileNames && turn.fileNames.some((name, idx) => name && (!turn.imageUrls || !turn.imageUrls[idx])) && (
                            <div className="mb-2 space-y-1">
                              {turn.fileNames.map((name, idx) => {
                                // Only show if there's no corresponding image preview
                                if (!name || (turn.imageUrls && turn.imageUrls[idx])) return null;
                                return (
                                  <div key={idx} className="flex items-center space-x-2 text-white/80 text-sm">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <span>{name}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                          {turn.userMessage.content.parts.map(p => p.text).join('') && (
                            <p className={`text-base ${turn.error ? 'text-red-900' : 'text-white'}`}>
                              {turn.userMessage.content.parts.map(p => p.text).join('')}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Error Display */}
                    {turn.error && (
                      <div className="flex justify-end mt-2">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-w-sm sm:max-w-md w-full text-sm">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-red-800">{turn.error.message}</p>
                            <button 
                              onClick={() => handleRetrySendMessage(turn.userMessage?.content.parts.map(p => p.text).join('') || '', turn.id)}
                              className="text-sm font-medium text-red-700 hover:text-red-900 hover:underline"
                            >
                              Retry
                            </button>
                          </div>
                          <div className="mt-2">
                            <button
                              onClick={() => {
                                setMessages(prev => prev.map(t =>
                                  t.id === turn.id
                                    ? { ...t, showErrorDetails: !t.showErrorDetails }
                                    : t
                                ));
                              }}
                              className="text-xs font-medium text-gray-600 hover:text-gray-900 flex items-center space-x-1"
                            >
                              <span>Insights</span>
                              <svg className={`w-3 h-3 transition-transform ${turn.showErrorDetails ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {turn.showErrorDetails && (
                              <div className="mt-2 p-2 bg-white rounded border border-gray-200 max-h-40 overflow-y-auto">
                                <pre className="text-xs text-gray-700 whitespace-pre-wrap break-all">
                                  <code>{turn.error.technicalDetails}</code>
                                </pre>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tool Invocations & Thinking Indicator */} 
                    {turn.isThinking ? (
                      <div className="flex items-center space-x-2 text-gray-500 mt-4">
                        <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                        <span>{thinkingMessage || 'Thinking...'}</span>
                      </div>
                    ) : (
                      <>
                        {turn.thoughts.length > 0 && (
                          <div className="mt-4">
                            <button
                              onClick={() => {
                                setMessages(prev => prev.map(t =>
                                  t.id === turn.id
                                    ? { ...t, showThoughts: !t.showThoughts }
                                    : t
                                ));
                              }}
                              className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                            >
                              <span>{turn.showThoughts ? 'Hide' : 'Show'} Thoughts</span>
                              <svg className={`w-4 h-4 transition-transform ${turn.showThoughts ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                            {turn.showThoughts && (
                              <div className="mt-2 space-y-2">
                                {turn.thoughts.map((thought, index) => (
                                  <div key={index} className="bg-gray-50 p-3 rounded-lg">
                                    <p className="font-semibold text-gray-800 mb-2">{thought.title}</p>
                                    <div className="text-sm text-gray-700 prose prose-sm max-w-none">
                                      <ReactMarkdown>{thought.content}</ReactMarkdown>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {(() => {
                          const formattedDuration = turn.thinkingDuration !== undefined
                            ? formatDuration(turn.thinkingDuration)
                            : null;

                          if (!formattedDuration && turn.toolInvocations.length === 0) {
                            return null;
                          }

                          return (
                            <div className="mt-4">
                              {turn.toolInvocations.length > 0 ? (
                                <>
                                  <button
                                    onClick={() => {
                                      setMessages(prev => prev.map(t =>
                                        t.id === turn.id
                                          ? { ...t, showTools: !t.showTools }
                                          : t
                                      ));
                                    }}
                                    className="flex items-center space-x-2 text-sm font-medium text-gray-600 hover:text-gray-900"
                                  >
                                    <span>
                                      {turn.showTools
                                        ? 'Hide Tool Usage'
                                        : formattedDuration || 'Show Tool Usage'
                                      }
                                    </span>
                                    <svg className={`w-4 h-4 transition-transform ${turn.showTools ? 'transform rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                  {turn.showTools && (
                                    <div className="mt-2 space-y-2">
                                      {turn.toolInvocations.map((invocation, index) => (
                                        <div key={index} className="bg-blue-50 p-3 rounded-lg">
                                          <p className="font-semibold text-blue-800 mb-2">Tool: {invocation.call.name}</p>
                                          <div className="text-sm text-blue-700">
                                            <div className="mb-3">
                                              <p className="font-medium mb-1">Arguments:</p>
                                              <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">
                                                {JSON.stringify(invocation.call.args, null, 2)}
                                              </pre>
                                            </div>
                                            <div>
                                              <p className="font-medium mb-1">Response:</p>
                                              <pre className="bg-white p-2 rounded border text-xs overflow-x-auto">
                                                {JSON.stringify(invocation.response, null, 2)}
                                              </pre>
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </>
                              ) : (
                                formattedDuration && (
                                  <div className="text-sm font-medium text-gray-600">
                                    <span>{formattedDuration}</span>
                                  </div>
                                )
                              )}
                            </div>
                          );
                        })()}
                      </>
                    )}

                    {/* Model Message */}
                    {turn.modelMessage && (
                      <div className="mt-4">
                        <div className="text-gray-900 prose max-w-none">
                          <ReactMarkdown>
                            {turn.modelMessage.content.parts.map(p => p.text).join('')}
                          </ReactMarkdown>
                        </div>

                        {/* Copy Button */}
                        <div className="flex justify-start mt-2">
                          <button
                            onClick={() => {
                              const text = turn.modelMessage?.content.parts.map(p => p.text).join('') || '';
                              navigator.clipboard.writeText(text).then(() => {
                                showToast('Response copied to clipboard', 'success');
                              }).catch(() => {
                                showToast('Failed to copy to clipboard', 'error');
                              });
                            }}
                            className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-2 transition-colors"
                            aria-label="Copy response"
                            title="Copy response"
                          >
                            <CopyIcon className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>
        </div>
        
        {/* Bottom Action Bar */}
        <div className="bg-white px-4 lg:px-6 pb-4">
          <div className="max-w-4xl mx-auto">
            {/* Unified Input & Connection Container */}
            <div
              className={`border rounded-lg bg-white focus-within:ring-2 focus-within:ring-netlify-teal focus-within:border-transparent relative ${isDragging ? 'border-teal-500 border-2 border-dashed bg-teal-50' : 'border-gray-300'}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {/* Drag overlay */}
              {isDragging && (
                <div className="absolute inset-0 flex items-center justify-center bg-teal-50/90 z-10 rounded-lg">
                  <div className="text-center">
                    <svg className="w-8 h-8 text-teal-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <p className="text-sm font-medium text-teal-700">Rilascia il file qui</p>
                  </div>
                </div>
              )}
              {/* Connection Panel - shown at top when needed */}
              {agentId && (
                <ConnectionManagerDrawer
                  agentId={agentId}
                  requiredProviders={requiredProviders}
                  onActiveConnectionsChange={setActiveConnections}
                />
              )}
              
              {/* File Preview - Multi-file grid */}
              {selectedFiles.length > 0 && (
                <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-gray-500">
                      {selectedFiles.length} file{selectedFiles.length > 1 ? 's' : ''} selezionato{selectedFiles.length > 1 ? 'i' : ''}
                    </span>
                    <button
                      onClick={clearSelectedFiles}
                      className="text-xs text-red-500 hover:text-red-700 transition-colors"
                    >
                      Rimuovi tutti
                    </button>
                  </div>
                  <div className={`grid gap-2 ${selectedFiles.length === 1 ? 'grid-cols-1' : selectedFiles.length <= 4 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="relative group flex items-center space-x-2 bg-white rounded-lg border border-gray-200 p-2">
                        {previewUrls[index] ? (
                          <img
                            src={previewUrls[index]}
                            alt={`Preview ${index + 1}`}
                            className="w-12 h-12 object-cover rounded border border-gray-200 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 truncate">{file.name}</p>
                          <p className="text-xs text-gray-400">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="p-1 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors opacity-0 group-hover:opacity-100"
                          title="Rimuovi file"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hidden file input - multiple files supported */}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,image/webp,image/bmp,application/pdf,.docx,.txt,.md"
                onChange={handleFileSelect}
                className="hidden"
              />

              {/* Message Input */}
              <div className="relative">
                <textarea
                  ref={textareaRef}
                  rows={1}
                  value={message}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    // Allow Enter to create new lines instead of sending
                    // Only send with Ctrl+Enter or Cmd+Enter
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  onPaste={handlePaste}
                  placeholder={selectedFiles.length > 0 ? "Aggiungi un messaggio o invia direttamente..." : "Message your agent (Ctrl+Enter to send)"}
                  className="w-full px-4 py-3 pr-24 bg-transparent border-0 text-gray-900 placeholder-gray-500 focus:outline-none resize-none max-h-60 overflow-y-auto"
                  style={{
                    minHeight: '3.5rem',
                    height: 'auto',
                    maxHeight: '15rem'
                  }}
                  onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
                    const target = e.target as HTMLTextAreaElement;
                    target.style.height = '3.5rem';
                    target.style.height = Math.min(target.scrollHeight, 240) + 'px';
                  }}
                />
                <div className="absolute right-2 top-4 flex items-center space-x-1">
                  {/* File upload button */}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="p-2 text-gray-400 hover:text-netlify-teal transition-colors"
                    title="Allega file (immagine o documento)"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                    </svg>
                  </button>
                  {/* Send button */}
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!message.trim() && selectedFiles.length === 0}
                    className="p-2 text-gray-400 hover:text-netlify-teal disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Invia messaggio"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Parameters Sidebar */}
      <QuickParametersSidebar
        isOpen={showParametersSidebar}
        onClose={() => setShowParametersSidebar(false)}
        agent={currentAgent}
        agentTools={agentTools}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </Layout>
  );
};

export default MainLayout;
